import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@medimo/ui";
import {
  autoSignIn,
  confirmSignUp,
  getCurrentUser,
  resendSignUpCode,
  signOut,
} from "aws-amplify/auth";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useWithLoading } from "./use-with-loading";

const ConfirmFormSchema = z.object({
  code: z.string().min(1, {
    message: "認証コードを入力してください",
  }),
});
export type ConfirmFormSchemaType = z.infer<typeof ConfirmFormSchema>;

// Cognito の例外メッセージは英語のまま利用者に見せられないため、種別ごとに日本語へ読み替える
const CONFIRM_ERROR_MESSAGES: Record<string, string> = {
  ExpiredCodeException: "入力した認証コードは有効期限切れです",
  CodeMismatchException: "認証コードが正しくありません",
  LimitExceededException:
    "試行回数の上限に達しました。しばらく時間をおいてお試しください",
  TooManyFailedAttemptsException:
    "試行回数の上限に達しました。しばらく時間をおいてお試しください",
};

export const useConfirmForm = ({
  email,
  onVerifyCode,
  navigateToHome,
  onSubmit,
  onConfirmed,
}: {
  email: string;
  onVerifyCode: VoidFunction;
  navigateToHome: VoidFunction;
  onSubmit?: VoidFunction;
  onConfirmed?: VoidFunction;
}) => {
  const { toast } = useToast();
  const form = useForm<ConfirmFormSchemaType>({
    resolver: zodResolver(ConfirmFormSchema),
    defaultValues: { code: "" },
  });

  const handleSubmit = useWithLoading(async (values: ConfirmFormSchemaType) => {
    onSubmit?.();
    // NOTE: sessionが残っていたらsignOutする
    try {
      await getCurrentUser().then(async () => {
        await signOut();
      });
    } catch {}
    return confirmSignUp({
      username: email,
      confirmationCode: values.code,
    })
      .then(async () => {
        onConfirmed?.();
        return await autoSignIn();
      })
      .then(onVerifyCode)
      .catch((e) => {
        if (
          e.message?.includes(
            "User cannot be confirmed. Current status is CONFIRMED",
          )
        ) {
          navigateToHome();
          return;
        }
        form.setError("code", {
          message:
            CONFIRM_ERROR_MESSAGES[e.name] ??
            "認証コードを確認できませんでした。もう一度お試しください",
        });
        // メッセージ本文は転送先のログに英語原文が残るため、例外の種別のみ記録する
        console.error("Confirm sign-up failed:", e.name);
      });
  });

  const handleResendSignUpCodeOnClick = async () => {
    try {
      await resendSignUpCode({ username: email });
      toast({
        title: "認証コードを再送信しました",
      });
    } catch (e) {
      console.error(
        "Resend sign-up code failed:",
        e instanceof Error ? e.name : e,
      );
      toast({
        variant: "destructive",
        title: "認証コードを再送信できませんでした",
        description: "しばらく時間をおいてもう一度お試しください。",
      });
    }
  };

  return {
    form,
    handleSubmit: form.handleSubmit(handleSubmit),
    handleResendSignUpCodeOnClick,
    isLoading: handleSubmit.isLoading,
  };
};
